"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Send, ImagePlus } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { useAuth } from '@/contexts/AuthContext';
import { Tables } from '@/integrations/supabase/types';
import { Profile } from '@/types/supabase';
import { Attachment } from '@/types';
import { generateId } from '@/lib/utils';
import InputAttachmentPreview from '@/components/chat/InputAttachmentPreview';
import EmojiPickerPopover from '@/components/lobby/EmojiPickerPopover';

const formSchema = z.object({
  content: z.string(), // No longer require min(1) as message can be just an image
});

type FormData = z.infer<typeof formSchema>;

interface MessageInputProps {
  onSendMessage: (content: string, files: File[]) => void;
  channelName: string;
  channelMembers: Profile[];
  profile: Tables<'profiles'> | null;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, channelName, channelMembers, profile }) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fileMap, setFileMap] = useState<Record<string, File>>({});

  const [mentionPopoverOpen, setMentionPopoverOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [filteredMembers, setFilteredMembers] = useState<Profile[]>([]);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { content: '' },
  });

  const { setValue, watch } = form;
  const contentValue = watch('content');

  useEffect(() => {
    if (mentionQuery && mentionQuery.length >= 3) {
      const lowerCaseQuery = mentionQuery.toLowerCase();
      setFilteredMembers(
        channelMembers.filter(member =>
          (member.full_name?.toLowerCase().includes(lowerCaseQuery) ||
           member.username?.toLowerCase().includes(lowerCaseQuery))
        )
      );
    } else {
      setFilteredMembers([]);
    }
  }, [mentionQuery, channelMembers]);

  const calculateCursorPosition = (textarea: HTMLTextAreaElement) => {
    const text = textarea.value.substring(0, textarea.selectionStart);
    const lines = text.split('\n');
    const currentLine = lines.length - 1;
    const currentColumn = lines[lines.length - 1].length;

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'pre';
    tempDiv.style.font = window.getComputedStyle(textarea).font;
    tempDiv.style.padding = window.getComputedStyle(textarea).padding;
    tempDiv.style.border = window.getComputedStyle(textarea).border;
    tempDiv.textContent = lines[lines.length - 1];
    document.body.appendChild(tempDiv);

    const textWidth = tempDiv.offsetWidth;
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);

    const textareaRect = textarea.getBoundingClientRect();
    const top = textareaRect.top + (currentLine * lineHeight) + 25;
    const left = textareaRect.left + Math.min(textWidth, textarea.offsetWidth - 200);

    document.body.removeChild(tempDiv);

    return { top, left };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const cursorPosition = e.target.selectionStart;
    setValue('content', text, { shouldValidate: true });

    const textBeforeCursor = text.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w+)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      setMentionStartIndex(mentionMatch.index!);
      
      const position = calculateCursorPosition(e.target);
      setCursorPosition(position);
      
      if (query.length >= 3) {
        setMentionPopoverOpen(true);
      } else {
        setMentionPopoverOpen(false);
      }
    } else {
      setMentionPopoverOpen(false);
    }
  };

  const handleMentionSelect = (member: Profile) => {
    const currentContent = form.getValues("content");
    const textBeforeMention = currentContent.substring(0, mentionStartIndex);
    const textAfterCursor = currentContent.substring(mentionStartIndex + mentionQuery.length + 1);
    
    const displayName = member.full_name || member.username || '';
    const newContent = `${textBeforeMention}@${displayName} ${textAfterCursor}`;
    form.setValue("content", newContent);
    setMentionPopoverOpen(false);

    setTimeout(() => {
      const newCursorPosition = textBeforeMention.length + displayName.length + 2;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
    setFileMap(prev => {
      const newMap = { ...prev };
      delete newMap[id];
      return newMap;
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    const id = generateId();
    const reader = new FileReader();
    reader.onload = (e) => {
      const newAttachment: Attachment = {
        id,
        type: 'image',
        content: e.target.result as string,
        name: file.name,
      };
      setAttachments(prev => [...prev, newAttachment]);
      setFileMap(prev => ({ ...prev, [id]: file }));
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = (values: FormData) => {
    if (!values.content.trim() && attachments.length === 0) {
      return; // Prevent sending empty messages
    }
    onSendMessage(values.content, Object.values(fileMap));
    form.reset({ content: '' });
    setAttachments([]);
    setFileMap({});
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!mentionPopoverOpen) {
        form.handleSubmit(onSubmit)();
      }
    }
  };

  const isAdmin = profile?.role === 'admin';
  const canSend = form.formState.isSubmitting || (!contentValue.trim() && attachments.length === 0);

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const currentContent = form.getValues("content");
    const cursorStart = textareaRef.current?.selectionStart || currentContent.length;
    const cursorEnd = textareaRef.current?.selectionEnd || currentContent.length;

    const newContent =
      currentContent.substring(0, cursorStart) +
      emojiData.emoji +
      currentContent.substring(cursorEnd);

    form.setValue("content", newContent, { shouldValidate: true });

    // Restore cursor position after emoji insertion
    setTimeout(() => {
      const newCursorPosition = cursorStart + emojiData.emoji.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <div className="bg-card">
      <InputAttachmentPreview attachments={attachments} onRemoveAttachment={handleRemoveAttachment} />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex items-start gap-2 p-2 border-x border-b rounded-b-md">
          {isAdmin && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0"
              >
                <ImagePlus className="h-5 w-5" />
                <span className="sr-only">Upload Image</span>
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                className="hidden"
                accept="image/*"
              />
            </>
          )}
          <EmojiPickerPopover onEmojiSelect={handleEmojiSelect} />
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Textarea
                    {...field}
                    onChange={handleInputChange}
                    ref={textareaRef}
                    placeholder={`Message #${channelName}`}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    className="min-h-[40px] max-h-[200px] resize-none"
                    onPaste={(e) => {
                      const items = e.clipboardData?.items;
                      if (!items) return;

                      for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        if (item.type && item.type.startsWith('image/')) {
                          const blob = item.getAsFile();
                          if (blob) {
                            const id = generateId();
                            const fileName = `pasted-image-${Date.now()}.${(blob.type.split('/')[1] || 'png')}`;
                            const file = new File([blob], fileName, { type: blob.type });

                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              const newAttachment: Attachment = {
                                id,
                                type: 'image',
                                content: ev.target?.result as string,
                                name: file.name,
                              };
                              setAttachments(prev => [...prev, newAttachment]);
                              setFileMap(prev => ({ ...prev, [id]: file }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" size="icon" disabled={canSend} className="flex-shrink-0">
            <Send className="h-5 w-5" />
            <span className="sr-only">Send</span>
          </Button>
        </form>
      </Form>
      {mentionPopoverOpen && filteredMembers.length > 0 && (
        <div 
          className="fixed z-50 w-60 bg-popover border rounded-md shadow-md"
          style={{
            top: `${cursorPosition.top}px`,
            left: `${cursorPosition.left}px`,
          }}
        >
          <Command>
            <CommandEmpty>No user found.</CommandEmpty>
            <CommandGroup>
              {filteredMembers.map((member) => (
                <CommandItem
                  key={member.id}
                  onSelect={() => handleMentionSelect(member)}
                  value={member.full_name || member.username || ''}
                  className="cursor-pointer"
                >
                  {member.full_name} {member.username && `(@${member.username})`}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </div>
      )}
    </div>
  );
};

export default MessageInput;